import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import type { Operator, Permission, Role } from "@/types/fleet";

/**
 * The current session's operator, from `GET /api/v1/auth/me`.
 *
 * **Fails closed.** The fork this console was lifted from fell back to a
 * synthetic admin with every permission whenever /whoami errored, on the theory
 * that the server enforces the real check anyway. That is true and still wrong:
 * it hands a viewer a console full of live-looking buttons that 403 on click,
 * and during an outage it does it to everyone at once. An unknown identity here
 * has **no** permissions; the UI degrades to read-only until the server says
 * otherwise.
 *
 * rest-api.md: "/api/v1/auth/me reads the role from the DB on every call — a
 * demotion takes effect immediately." So this is refetched on focus rather than
 * cached for the session: a revoked operator should lose the buttons on their
 * next glance at the tab, not on their next login.
 */
export function useWhoami() {
  const query = useQuery<Operator, ApiError>({
    queryKey: qk.me,
    queryFn: () => get<Operator>("/auth/me"),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: (count, err) => err.status !== 401 && count < 2,
  });

  const me = query.data ?? null;
  const permissions: readonly Permission[] = me?.permissions ?? [];

  return {
    ...query,
    me,
    role: (me?.role ?? null) as Role | null,
    username: me?.username ?? null,
    /** Unknown identity ⇒ false. Always. */
    hasPermission(p: Permission): boolean {
      return permissions.includes(p);
    },
    /** True once the server has actually answered. */
    isKnown: me !== null,
  };
}

/** Drop the cached identity — used after logout so the shell re-gates instantly. */
export function useForgetIdentity() {
  const qc = useQueryClient();
  return () => {
    qc.removeQueries({ queryKey: qk.me });
    qc.clear();
  };
}
